import { Clock } from 'lucide-react';
import { useMemo, useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import type { LessonStep, KeyInsight } from '@/types/lesson';
import { Diagram } from './Diagram';
import { TabSection, ResourceList, KeyInsightView } from './TabSection';

interface LessonContentProps {
  title: string;
  module: string;
  theory: string;
  keyInsight?: string | KeyInsight;
  estimatedTime?: number;
  steps?: LessonStep[];
}

/**
 * Компонент для отображения теоретического содержания урока
 */
export function LessonContent({
  title,
  module,
  theory,
  keyInsight,
  estimatedTime,
  steps,
}: LessonContentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Анимация появления при монтировании
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Улучшенный рендеринг markdown с поддержкой code blocks, таблиц и диаграмм
  const renderMarkdown = useMemo(() => {
    return (text: string) => {
      const elements: React.ReactNode[] = [];
      const lines = text.split('\n');
      let i = 0;
      let key = 0;

      while (i < lines.length) {
        const line = lines[i];
        if (!line) {
          i++;
          continue;
        }

        // Diagram blocks (:::diagram type:::)
        if (line.startsWith(':::diagram')) {
          const diagramType = line.replace(':::diagram', '').trim().replace(':::', '').trim();
          const validTypes = [
            'three-trees',
            'git-flow',
            'commit-structure',
            'branch-merge',
            'staging-flow',
            'reset-modes',
            'remote-workflow',
            'rebase-vs-merge',
          ] as const;

          if (validTypes.includes(diagramType as (typeof validTypes)[number])) {
            elements.push(
              <Diagram key={key++} type={diagramType as (typeof validTypes)[number]} />
            );
          }
          i++;
          continue;
        }

        // Code blocks (``` ... ```)
        if (line.startsWith('```')) {
          const language = line.slice(3).trim();
          const codeLines: string[] = [];
          i++;

          while (i < lines.length) {
            const currentLine = lines[i];
            if (!currentLine || currentLine.startsWith('```')) break;
            codeLines.push(currentLine);
            i++;
          }

          elements.push(
            <div key={key++} className="my-4 overflow-hidden rounded-lg border border-slate-700">
              {language && (
                <div className="bg-slate-800 px-3 py-1 text-xs text-slate-400 border-b border-slate-700">
                  {language}
                </div>
              )}
              <pre className="bg-slate-900 p-4 overflow-x-auto">
                <code className="font-mono text-sm text-slate-300 whitespace-pre">
                  {codeLines.join('\n')}
                </code>
              </pre>
            </div>
          );
          i++; // Skip closing ```
          continue;
        }

        // Tables
        if (line.includes('|') && i + 1 < lines.length) {
          const nextLine = lines[i + 1];
          if (nextLine && nextLine.match(/^\|[\s\-:|]+\|/)) {
            const tableRows: string[][] = [];

            // Parse header
            const headerCells = line.split('|').filter((cell) => cell.trim());
            tableRows.push(headerCells.map((cell) => cell.trim()));

            i++; // Skip separator line
            i++; // Move to first data row

            // Parse data rows
            while (i < lines.length) {
              const currentLine = lines[i];
              if (!currentLine || !currentLine.includes('|')) break;
              const cells = currentLine.split('|').filter((cell) => cell.trim());
              tableRows.push(cells.map((cell) => cell.trim()));
              i++;
            }

            const headerRow = tableRows[0];
            if (!headerRow) {
              i++;
              continue;
            }

            elements.push(
              <div key={key++} className="my-4 overflow-x-auto">
                <table className="min-w-full border-collapse border border-slate-700 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-slate-800">
                      {headerRow.map((cell, cellIndex) => (
                        <th
                          key={cellIndex}
                          className="border border-slate-700 px-4 py-2 text-left text-sm font-semibold text-slate-200"
                        >
                          {renderInlineMarkdown(cell)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex} className="bg-slate-900/50">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="border border-slate-700 px-4 py-2 text-sm text-slate-300"
                          >
                            {renderInlineMarkdown(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            continue;
          }
        }

        // Заголовки
        if (line.startsWith('# ')) {
          elements.push(
            <h1 key={key++} className="mb-4 mt-6 text-2xl font-bold text-slate-200">
              {line.slice(2)}
            </h1>
          );
          i++;
          continue;
        }
        if (line.startsWith('## ')) {
          elements.push(
            <h2 key={key++} className="mb-3 mt-5 text-xl font-semibold text-slate-200">
              {line.slice(3)}
            </h2>
          );
          i++;
          continue;
        }
        if (line.startsWith('### ')) {
          elements.push(
            <h3 key={key++} className="mb-2 mt-4 text-lg font-semibold text-slate-200">
              {line.slice(4)}
            </h3>
          );
          i++;
          continue;
        }

        // Списки
        if (line.startsWith('- ')) {
          elements.push(
            <li key={key++} className="ml-4 list-disc text-slate-300">
              {renderInlineMarkdown(line.slice(2))}
            </li>
          );
          i++;
          continue;
        }
        if (line.match(/^\d+\. /)) {
          const content = line.replace(/^\d+\. /, '');
          elements.push(
            <li key={key++} className="ml-4 list-decimal text-slate-300">
              {renderInlineMarkdown(content)}
            </li>
          );
          i++;
          continue;
        }

        // Пустые строки
        if (line.trim() === '') {
          elements.push(<div key={key++} className="h-3" />);
          i++;
          continue;
        }

        // Обычный текст
        elements.push(
          <p key={key++} className="mb-2 leading-relaxed text-slate-300">
            {renderInlineMarkdown(line)}
          </p>
        );
        i++;
      }

      return elements;
    };
  }, [theory]);

  // Рендеринг inline markdown (жирный, код)
  const renderInlineMarkdown = (text: string): React.ReactNode => {
    // **bold**
    const parts = text.split(/(\*\*[^*]+\*\*)/);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-semibold text-slate-200">
            {part.slice(2, -2)}
          </strong>
        );
      }
      // `code`
      const codeParts = part.split(/(`[^`]+`)/);
      return codeParts.map((codePart, j) => {
        if (codePart.startsWith('`') && codePart.endsWith('`')) {
          return (
            <code
              key={`${i}-${j}`}
              className="rounded bg-slate-700 px-1.5 py-0.5 font-mono text-sm text-git-orange"
            >
              {codePart.slice(1, -1)}
            </code>
          );
        }
        return codePart;
      });
    });
  };

  return (
    <div
      ref={contentRef}
      className={cn(
        'p-6 bg-slate-900 min-h-full',
        'transition-opacity duration-300',
        isVisible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Заголовок урока */}
      <div
        className={cn(
          'mb-6',
          'transition-[transform,opacity] duration-500',
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        )}
      >
        <p className="mb-1 text-sm font-medium text-git-orange">{module}</p>
        <h1 className="text-2xl font-bold text-slate-100">{title}</h1>
        {estimatedTime && (
          <div className="mt-2 flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" />
            <span>{estimatedTime} мин</span>
          </div>
        )}
      </div>

      {/* Tab Section для контента урока */}
      <TabSection
        theoryContent={<div className="max-w-none">{renderMarkdown(theory)}</div>}
        practiceContent={
          steps && steps.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">
                Выполните команды в интерактивном блоке справа, чтобы завершить этот урок.
              </p>
              <div className="space-y-2 stagger-animation">
                {steps.map((step) => (
                  <div
                    key={step.id}
                    className={cn(
                      'flex items-start gap-3 rounded-lg bg-slate-800 p-3',
                      'transition-[background-color,transform] duration-200',
                      'hover:bg-slate-700 hover:translate-x-1'
                    )}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-git-orange text-xs font-medium text-white">
                      {step.id}
                    </span>
                    <div>
                      <p className="font-medium text-slate-200">{step.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{step.description}</p>
                      <code className="mt-1 inline-block rounded bg-slate-700 px-2 py-0.5 font-mono text-xs text-git-orange">
                        {step.expectedCommand}
                      </code>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : undefined
        }
        insightsContent={keyInsight ? <KeyInsightView insight={keyInsight} /> : undefined}
        resourcesContent={
          <ResourceList
            resources={[
              {
                title: 'Официальная документация Git',
                url: 'https://git-scm.com/doc',
                description: 'Полная документация по Git на официальном сайте',
                type: 'docs',
              },
              {
                title: 'Pro Git Book',
                url: 'https://git-scm.com/book/ru/v2',
                description: 'Бесплатная книга Pro Git на русском языке',
                type: 'article',
              },
              {
                title: 'Git Cheat Sheet',
                url: 'https://training.github.com/downloads/ru/github-git-cheat-sheet/',
                description: 'Шпаргалка по основным командам Git',
                type: 'article',
              },
            ]}
          />
        }
        defaultTab="theory"
        className={cn(
          'transition-[transform,opacity] duration-500 delay-100',
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        )}
      />
    </div>
  );
}
